import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import { FiRefreshCw, FiSearch, FiShield } from "react-icons/fi";
import "./AccountingPage.css";
import SalesPostingPanel from "./SalesPostingPanel";
const input =
  "mt-2 min-h-11 w-full rounded-xl border border-slate-200 bg-white p-3 text-sm outline-none focus:border-primary-700";
const button =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-40";
const blank = () => ({
  placeOfSupply: "",
  taxLines: [],
  reviewNote: "",
  evidenceReference: "",
});
const Field = ({ label, children }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    {children}
  </label>
);
const dateLabel = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(new Date(value)) + " IST";
export default function SalesSnapshots() {
  const { accessToken, user } = useAuth(),
    canReview = hasPermission(user, "accounting.vouchers_post"),
    canVoid = canReview && hasPermission(user, "pos.void_sale"),
    canReturn = canReview && hasPermission(user, "pos.return");
  const [companies, setCompanies] = useState([]),
    [company, setCompany] = useState(""),
    [receipts, setReceipts] = useState([]),
    [search, setSearch] = useState(""),
    [saleId, setSaleId] = useState(""),
    [candidate, setCandidate] = useState(null),
    [form, setForm] = useState(blank),
    [preview, setPreview] = useState(null),
    [verified, setVerified] = useState(false),
    [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const generation = useRef(0),
    requestKey = useRef(null),
    scope = useRef("");
  const key = company + ":" + saleId;
  scope.current = key;
  useEffect(() => {
    let active = true;
    apiRequest("/accounting/companies", { accessToken })
      .then((r) => {
        if (active) {
          setCompanies(r);
          setCompany(r[0]?.id || "");
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);
  useEffect(() => {
    let active = true;
    setReceipts([]);
    setSaleId("");
    setCandidate(null);
    setForm(blank());
    setPreview(null);
    setVerified(false);
    setNotice("");
    if (company)
      apiRequest(`/accounting/${company}/sales-snapshots`, { accessToken })
        .then((r) => {
          if (active) setReceipts(r);
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    return () => {
      active = false;
    };
  }, [company, accessToken]);
  const load = useCallback(async () => {
    const g = ++generation.current;
    setCandidate(null);
    setPreview(null);
    setVerified(false);
    setForm(blank());
    requestKey.current = null;
    if (!company || !saleId) return;
    setLoading(true);
    setError("");
    try {
      const r = await apiRequest(
        `/accounting/${company}/sales-snapshots/pos/${saleId}`,
        { accessToken },
      );
      if (g === generation.current) {
        setCandidate(r);
        setForm({
          ...blank(),
          taxLines: r.source.items.map((i) => ({
            saleItemId: i.id,
            treatment: "",
            cgst: "",
            sgst: "",
            igst: "",
          })),
        });
      }
    } catch (e) {
      if (g === generation.current)
        setError(e.message || "Unable to load receipt");
    } finally {
      if (g === generation.current) setLoading(false);
    }
  }, [company, saleId, accessToken]);
  useEffect(() => {
    const requestGeneration = generation;
    load();
    return () => {
      requestGeneration.current++;
    };
  }, [load]);
  function change(next) {
    setForm(next);
    setPreview(null);
    setVerified(false);
    requestKey.current = null;
    setNotice("");
  }
  async function searchReceipts(e) {
    e.preventDefault();
    const c = company;
    setBusy(true);
    setError("");
    try {
      const r = await apiRequest(
        `/accounting/${c}/sales-snapshots?search=${encodeURIComponent(search.trim())}`,
        { accessToken },
      );
      if (scope.current.startsWith(c + ":")) setReceipts(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function review(e) {
    e.preventDefault();
    if (busy || !candidate) return;
    const expectedScope = key;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await apiRequest(
        `/accounting/${company}/sales-snapshots/pos/${saleId}/preview`,
        {
          method: "POST",
          accessToken,
          body: {
            ...form,
            ...(candidate.latestSnapshotId
              ? { expectedSnapshotId: candidate.latestSnapshotId }
              : {}),
          },
        },
      );
      if (scope.current === expectedScope) {
        setPreview(r);
        setVerified(false);
        requestKey.current = null;
      }
    } catch (e) {
      if (scope.current === expectedScope) setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    if (!preview || !verified || busy) return;
    const expectedScope = key;
    setBusy(true);
    setError("");
    try {
      if (typeof window.crypto?.randomUUID !== "function")
        throw new Error(
          "Use secure HTTPS or localhost to save a review safely.",
        );
      if (!requestKey.current) requestKey.current = window.crypto.randomUUID();
      await apiRequest(
        `/accounting/${company}/sales-snapshots/pos/${saleId}/reviews`,
        {
          method: "POST",
          accessToken,
          body: {
            ...form,
            ...(candidate.latestSnapshotId
              ? { expectedSnapshotId: candidate.latestSnapshotId }
              : {}),
            requestKey: requestKey.current,
            reviewHash: preview.reviewHash,
            reviewed: true,
          },
        },
      );
      if (scope.current === expectedScope) {
        await load();
        setNotice(
          "Reviewed snapshot saved. No voucher, inventory, customer due or payment was created.",
        );
      }
    } catch (e) {
      if (scope.current === expectedScope) setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const linesReady =
    form.taxLines.length > 0 &&
    form.taxLines.every(
      (l) =>
        l.treatment &&
        ["cgst", "sgst", "igst"].every((k) => /^\d+(\.\d{1,2})?$/.test(l[k])),
    );
  return (
    <main className="accounting-master space-y-6">
      <header>
        <p className="text-sm text-slate-500">
          Accounting / Sales Snapshot Review
        </p>
        <h1 className="mt-1 text-3xl font-bold text-slate-900">
          Sales Snapshot Review
        </h1>
        <p className="mt-2 text-slate-500">
          Review original tax and lot costs, then explicitly preview and post
          eligible receipts.
        </p>
      </header>
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950">
        Snapshot review does not change balances. Financial posting is a
        separate explicit action below. Check tax treatment and place of supply
        against original records. No automatic tax split or historical backfill.
      </div>
      {error && (
        <p
          role="alert"
          className="rounded-xl bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="rounded-xl bg-green-50 p-4 text-sm text-green-800"
        >
          {notice}
        </p>
      )}
      <section className="space-y-4 rounded-2xl border bg-white p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Accounting company">
            <select
              className={input}
              disabled={busy}
              value={company}
              onChange={(e) => {
                setCompany(e.target.value);
                setError("");
              }}
            >
              <option value="">Select company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.name}
                </option>
              ))}
            </select>
          </Field>
          <form onSubmit={searchReceipts} className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Find POS receipt">
                <input
                  className={input}
                  maxLength={50}
                  value={search}
                  disabled={busy}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Receipt number"
                />
              </Field>
            </div>
            <button className={button} disabled={busy || !company}>
              <FiSearch />
              Search
            </button>
          </form>
        </div>
        <Field label="POS receipt (latest 50 matching records)">
          <select
            className={input}
            disabled={busy}
            value={saleId}
            onChange={(e) => {
              setSaleId(e.target.value);
              setNotice("");
              setError("");
            }}
          >
            <option value="">Select receipt</option>
            {receipts.map((r) => (
              <option key={r.id} value={r.id}>
                {r.receiptNumber} · {r.store.code} · {dateLabel(r.completedAt)}
                {r.accountingSnapshots.length
                  ? " · Review history v" + r.accountingSnapshots[0].sequence
                  : ""}
              </option>
            ))}
          </select>
        </Field>
        {!receipts.length && (
          <p className="text-xs text-slate-500">
            No matching completed receipts in mapped company stores. Check
            Posting Setup or search for a receipt number.
          </p>
        )}
      </section>
      {loading && <p role="status">Loading source evidence…</p>}
      {candidate && (
        <>
          <SalesPostingPanel
            key={
              key +
              ":" +
              candidate.latestSnapshotId +
              ":" +
              candidate.sourceHash
            }
            companyId={company}
            saleId={saleId}
            candidate={candidate}
            accessToken={accessToken}
            canPost={canReview}
            canVoid={canVoid && candidate?.costs?.accountingBasis!=='SIS_COST_ALREADY_ACCRUED'}
            canReturn={canReturn && candidate?.costs?.accountingBasis!=='SIS_COST_ALREADY_ACCRUED'}
            onPosted={() => {
              if (scope.current === key) load();
            }}
          />
          <section className="space-y-4 rounded-2xl border bg-white p-5">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold">
                  {candidate.source.receiptNumber}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {dateLabel(candidate.source.completedAt)} ·{" "}
                  {candidate.source.items.length} sale lines
                </p>
              </div>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm"
                disabled={busy}
                onClick={load}
              >
                <FiRefreshCw />
                Reload source
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ["Sale total", candidate.reconciliation.total],
                ["Original combined tax", candidate.reconciliation.combinedTax],
                [
                  "Traceable original cost",
                  candidate.costs.totalCost ?? "Needs review",
                ],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="mt-1 font-semibold">{value}</p>
                </div>
              ))}
            </div>
            {!!candidate.blockers.length && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <h3 className="font-semibold text-red-900">
                  Source review blocked
                </h3>
                <ul className="mt-2 space-y-1 text-xs text-red-800">
                  {candidate.blockers.map((b, i) => (
                    <li key={i}>{b.replaceAll("_", " ")}</li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-red-800">
                  Resolve the source evidence or ownership issue first. This
                  screen cannot override these safeguards.
                </p>
              </div>
            )}
            <details>
              <summary className="cursor-pointer text-sm font-semibold text-primary-700">
                Original lot-cost evidence
              </summary>
              <div className="mt-3 space-y-3">
                {candidate.costs.details.map((line) => (
                  <div
                    key={line.saleItemId}
                    className="rounded-xl border p-3 text-xs"
                  >
                    <p className="font-semibold">
                      Line {line.saleItemId} · Recorded cost {line.cost}
                    </p>
                    <p className="mt-1 break-all text-slate-400">
                      Movement {line.movementId}
                    </p>
                    {line.allocations.map((a) => (
                      <p key={a.allocationId} className="mt-2 text-slate-600">
                        Lot {a.lotNumber} · Quantity {a.quantity} · Original
                        unit cost {a.unitCost} · Outright
                        <br />
                        Receipt {a.receiptId} · Purchase voucher{" "}
                        {a.purchaseVoucherId}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          </section>
          {canReview &&
            !candidate.accountingSource &&
            !candidate.blockers.length && (
              <form
                onSubmit={review}
                className="space-y-5 rounded-2xl border bg-white p-5"
              >
                <fieldset disabled={busy} className="space-y-5">
                  <h2 className="text-lg font-bold">
                    Accountant’s tax and cost review
                  </h2>
                  <Field label="Reviewed place of supply">
                    <select
                      required
                      className={input}
                      value={form.placeOfSupply}
                      onChange={(e) =>
                        change({ ...form, placeOfSupply: e.target.value })
                      }
                    >
                      <option value="">Select reviewed state</option>
                      {Object.entries(candidate.states).map(([code, name]) => (
                        <option key={code} value={code}>
                          {code} · {name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <p className="text-xs text-slate-500">
                    Selling registration state: {candidate.registrationState}.
                    All components must equal the original line tax. This review
                    does not certify statutory tax eligibility or filing
                    treatment.
                  </p>
                  {form.taxLines.map((line, index) => {
                    const item = candidate.source.items[index];
                    return (
                      <div
                        key={line.saleItemId}
                        className="space-y-3 rounded-xl border border-slate-200 p-4"
                      >
                        <h3 className="font-semibold">
                          {item.productName} · {item.sku}
                        </h3>
                        <p className="text-xs text-slate-500">
                          Quantity {item.quantity} · Original line total{" "}
                          {item.lineTotalAmount} · Tax {item.lineTaxAmount}
                        </p>
                        <Field label={`Treatment for ${item.sku}`}>
                          <select
                            required
                            className={input}
                            value={line.treatment}
                            onChange={(e) =>
                              change({
                                ...form,
                                taxLines: form.taxLines.map((l, i) =>
                                  i === index
                                    ? { ...l, treatment: e.target.value }
                                    : l,
                                ),
                              })
                            }
                          >
                            <option value="">Select explicit treatment</option>
                            <option value="INTRA_STATE">
                              Intra-state CGST + SGST
                            </option>
                            <option value="INTER_STATE">
                              Inter-state IGST
                            </option>
                            <option value="ZERO_TAX">
                              Original line has zero tax
                            </option>
                          </select>
                        </Field>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {["cgst", "sgst", "igst"].map((k) => (
                            <Field
                              key={k}
                              label={`${k.toUpperCase()} for ${item.sku}`}
                            >
                              <input
                                required
                                inputMode="decimal"
                                pattern="[0-9]+(\.[0-9]{1,2})?"
                                className={input}
                                value={line[k]}
                                placeholder="Enter amount, including 0"
                                onChange={(e) =>
                                  change({
                                    ...form,
                                    taxLines: form.taxLines.map((l, i) =>
                                      i === index
                                        ? { ...l, [k]: e.target.value }
                                        : l,
                                    ),
                                  })
                                }
                              />
                            </Field>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  <Field label="Evidence reference">
                    <input
                      required
                      minLength={3}
                      maxLength={150}
                      className={input}
                      value={form.evidenceReference}
                      onChange={(e) =>
                        change({ ...form, evidenceReference: e.target.value })
                      }
                      placeholder="Original receipt / reviewed document reference"
                    />
                  </Field>
                  <Field label="Tax, ownership and prior-entry review note">
                    <textarea
                      required
                      minLength={20}
                      maxLength={1000}
                      rows={3}
                      className={input}
                      value={form.reviewNote}
                      onChange={(e) =>
                        change({ ...form, reviewNote: e.target.value })
                      }
                    />
                  </Field>
                  <button
                    className={button}
                    disabled={
                      busy ||
                      !linesReady ||
                      !form.placeOfSupply ||
                      form.reviewNote.trim().length < 20 ||
                      form.evidenceReference.trim().length < 3
                    }
                  >
                    <FiShield />
                    Preview reviewed snapshot
                  </button>
                </fieldset>
              </form>
            )}
          {!canReview && (
            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
              View-only access. Saving reviews requires accounting posting
              permission.
            </p>
          )}
          {preview && (
            <section className="space-y-4 rounded-2xl border border-green-200 bg-white p-5">
              <h2 className="font-bold">Review before saving</h2>
              <p className="text-sm text-slate-600">
                CGST {preview.snapshot.taxTotals.cgst} · SGST{" "}
                {preview.snapshot.taxTotals.sgst} · IGST{" "}
                {preview.snapshot.taxTotals.igst} · Recorded cost{" "}
                {preview.snapshot.costs.totalCost}
              </p>
              <p className="text-xs text-slate-500">
                {form.evidenceReference} · {form.reviewNote}
              </p>
              <label className="flex items-start gap-3 text-sm leading-6">
                <input
                  type="checkbox"
                  disabled={busy}
                  checked={verified}
                  onChange={(e) => setVerified(e.target.checked)}
                  className="mt-1"
                />
                I reviewed the original receipt tax, lot ownership/costs and
                prior entries. Save this immutable review only; do
                not post accounting.
              </label>
              <button
                type="button"
                className={button}
                disabled={busy || !verified}
                onClick={save}
              >
                {busy ? "Saving…" : "Save Reviewed Snapshot"}
              </button>
            </section>
          )}
          <section className="space-y-3 rounded-2xl border bg-white p-5">
            <h2 className="font-bold">Immutable review history</h2>
            <p className="text-sm text-slate-500">
              {candidate.latestSnapshotId
                ? candidate.latestSnapshotCurrent
                  ? "Latest review matches current source evidence. Posting remains disabled."
                  : "Source or setup changed since the latest review. A fresh review is required."
                : "No reviewed snapshot yet."}
            </p>
            {candidate.history.map((h) => (
              <details key={h.id} className="rounded-xl border p-3">
                <summary className="cursor-pointer text-sm font-semibold">
                  Version {h.sequence} · {dateLabel(h.createdAt)}
                </summary>
                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  <p>Evidence: {h.snapshot.evidenceReference}</p>
                  <p>{h.snapshot.reviewNote}</p>
                  <p>
                    Place of supply: {h.snapshot.placeOfSupply} · CGST{" "}
                    {h.snapshot.taxTotals.cgst} · SGST{" "}
                    {h.snapshot.taxTotals.sgst} · IGST{" "}
                    {h.snapshot.taxTotals.igst}
                  </p>
                  <p>Recorded original cost: {h.snapshot.costs.totalCost}</p>
                  <p className="break-all text-slate-400">
                    Reviewer user ID {h.createdBy} · Source fingerprint{" "}
                    {h.sourceHash}
                  </p>
                </div>
              </details>
            ))}
          </section>
        </>
      )}
    </main>
  );
}
